import { inject } from '@angular/core';
import { optionalGender } from '@anihis/shared/src/functions/optional-gender';
import { MatDialog } from '@angular/material/dialog';
import {
  AnimalDto,
  AnimalsService,
  Gender,
  OwnerDto,
  UsersService,
  VeterinarianDto,
} from '@anihis/core/data-access';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { catchError, finalize, of, switchMap, take, tap } from 'rxjs';
import { InfoAnimalDialogComponent } from './info-animal-dialog/info-animal-dialog.component';
import { AddEditReportDialogComponent } from '../add-edit-report-dialog/add-edit-report-dialog.component';
import { InfoOwnerDialogComponent } from './info-owner-dialog/info-owner-dialog.component';
import { OwnerCardStateService } from '../../../shared/services/owner-card-state.service';
import { TabsStateService } from '../../../shared/services/tabs-state.service';
import { AddFormDialogComponent } from './add-form-dialog/add-form-dialog.component';
import { LoadingStatus } from '../../../shared/enums/loading';
import { TourStore } from '../../../shared/component/tour/tour.store';
import { ConfirmationDialogComponent } from '../../../shared/component/confirmation-dialog/confirmation-dialog.component';
import { ShellStore } from '../../../shell/shell.store';
import { TranslocoService } from '@jsverse/transloco';
import { PORTAL_DIALOG } from '../../../shared/constants/dialog.constants';
import { ReportSubscriptionGuardService } from '../../../shared/services/report-subscription-guard.service';
import { OwnerEditDialogComponent } from '../../owner/owner-edit-dialog/owner-edit-dialog.component';
import { AddEditOwnerDialogComponent } from '../../../shared/component/add-edit-owner-dialog/add-edit-owner-dialog.component';
import { DateTime } from 'luxon';

type CardHistoryState = {
  veterinarians: VeterinarianDto[] | undefined;
  animalUid: string | undefined;
  animal: AnimalDto | undefined;
  ownerUid: string | undefined;
  owner: OwnerDto | undefined;
  statusOwner: LoadingStatus | undefined;
  statusAnimal: LoadingStatus | undefined;

  refreshReports: number;
  refreshForms: number;
  refreshDocuments: number;
  reportUid: string | undefined;
  isFormDialogOpening?: boolean;
  selectedTabIndex: number;
};

const initialState: CardHistoryState = {
  veterinarians: undefined,
  animalUid: undefined,
  animal: undefined,
  ownerUid: undefined,
  owner: undefined,
  statusOwner: undefined,
  statusAnimal: undefined,
  refreshReports: 0,
  refreshForms: 0,
  refreshDocuments: 0,
  reportUid: undefined,
  isFormDialogOpening: false,
  selectedTabIndex: 0,
};

export const CardHistoryStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      animalsService = inject(AnimalsService),
      usersService = inject(UsersService),
      dialog = inject(MatDialog),
      ownerCardStateService = inject(OwnerCardStateService),
      tabsStateService = inject(TabsStateService),
      tour = inject(TourStore),
      shellStore = inject(ShellStore),
      transloco = inject(TranslocoService),
      reportSubscriptionGuard = inject(ReportSubscriptionGuardService),
    ) => {
      return {
        setOwnerAnimalUids(ownerUid: string, animalUid: string) {
          patchState(store, {
            animalUid,
            ownerUid,
            statusAnimal: LoadingStatus.Loading,
            statusOwner: LoadingStatus.Loading,
          });

          usersService
            .usersOwnersUidGet(ownerUid)
            .pipe(
              tap((x) => {
                patchState(store, {
                  owner: x.owner,
                });
              }),

              finalize(() => {
                patchState(store, {
                  statusOwner: LoadingStatus.Loaded,
                });
              }),
            )
            .subscribe();

          animalsService
            .animalsUidGet(animalUid)
            .pipe(
              tap((response) => {
                patchState(store, {
                  animal: response.animal,
                });
              }),

              finalize(() => {
                patchState(store, {
                  statusAnimal: LoadingStatus.Loaded,
                });
              }),
            )
            .subscribe();
        },

        infoAnimalDialog(animal: any, owner: any) {
          const updatedAnimal = {
            ...animal,
            owner: {
              ...animal.owner,
              firstName: owner.firstName,
              lastName: owner.lastName,
            },
          };

          dialog.open(InfoAnimalDialogComponent, {
            width: '643px',
            data: { ...updatedAnimal },
            autoFocus: false,
          });
        },

        openAddReportDialog(data?: { owner?: { unpaidExpenses?: number } }) {
          reportSubscriptionGuard
            .confirmReportAccess()
            .pipe(take(1))
            .subscribe((allowed) => {
              if (!allowed) return;
              this.openAddReportDialogAfterSubscriptionCheck(data);
            });
        },

        openAddReportDialogAfterSubscriptionCheck(data?: {
          owner?: { unpaidExpenses?: number };
        }) {
          // Prefer store.owner() (fresh from GET Users/Owners) over animal's owner field
          const unpaid = Number(
            store.owner()?.unpaidExpenses ?? data?.owner?.unpaidExpenses ?? 0,
          );
          const limit = Number(shellStore.debt()?.debtLimit ?? 0);
          const fmt0 = (v: number) =>
            new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
              v,
            );

          const hasLimit = Number.isFinite(limit) && limit > 0;
          const nearThreshold = hasLimit ? limit * 0.9 : Infinity;

          if (hasLimit && unpaid > limit) {
            const title = transloco.translate('debt.overLimit.title');
            const text = transloco.translate('debt.overLimit.text', {
              debt: fmt0(unpaid),
              limit: fmt0(limit),
            });

            const ref = dialog.open(ConfirmationDialogComponent, {
              width: '400px',
              data: { title, text },
              disableClose: true,
            });

            ref
              .afterClosed()
              .pipe(take(1))
              .subscribe((res) => {
                if (res?.confirmed) this.reportDialog(data);
              });
          } else if (hasLimit && unpaid >= nearThreshold && unpaid < limit) {
            const remaining = Math.max(0, limit - unpaid);
            const percent = Math.round((unpaid / limit) * 100);

            const title = transloco.translate('debt.nearLimit.title');
            const text = transloco.translate('debt.nearLimit.text', {
              debt: fmt0(unpaid),
              limit: fmt0(limit),
              remaining: fmt0(remaining),
              percent: String(percent),
            });

            const ref = dialog.open(ConfirmationDialogComponent, {
              width: '400px',
              data: { title, text },
              disableClose: true,
            });

            ref
              .afterClosed()
              .pipe(take(1))
              .subscribe((res) => {
                if (res?.confirmed) this.reportDialog(data);
              });
          } else {
            this.reportDialog(data);
          }
        },
        reportDialog(data?: any) {
          const dialogRef = dialog.open(AddEditReportDialogComponent, {
            ...PORTAL_DIALOG.wide,
            height: '85vh',
            disableClose: true,
            data: { ...data },
            autoFocus: false,
          });

          dialogRef.afterClosed().subscribe((result) => {
            patchState(store, {
              refreshReports: store.refreshReports() + 1,
              refreshDocuments: store.refreshDocuments() + 1,
            });
            if (result) {
              patchState(store, { selectedTabIndex: 0 });
            }
          });
        },

        infoOwnerDialog(owner: OwnerDto, animal: AnimalDto) {
          const dialogRef = dialog.open(InfoOwnerDialogComponent, {
            ...PORTAL_DIALOG.narrow,
            data: { ...owner, animal: animal },
            autoFocus: false,
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              // Persist identifiers first so the Owners tab initializes in detail view
              ownerCardStateService.setUids(
                result.animalUid,
                result.ownerUid as string,
                false,
              );

              localStorage.setItem('ownerUid', result.ownerUid);
              localStorage.setItem('animalUid', result.animalUid);

              tabsStateService.createOpenTabs('Owners');
            }
          });
        },

        toUTCISOString(
          date: Date | string | null | undefined,
        ): string | undefined {
          if (!date) return undefined;
          return DateTime.fromJSDate(new Date(date))
            .startOf('day')
            .toUTC()
            .toISO()!;
        },

        editOwner(dataForms: any) {
          patchState(store, { statusOwner: LoadingStatus.Loading });

          return usersService
            .usersOwnersUidPutForm(
              dataForms.formOwner.ownerUid,
              undefined,
              dataForms.formOwner.warning ?? undefined,
              undefined,
              dataForms.ownerData?.profilePicture?.uid,
              dataForms.formOwner.email,
              dataForms.formOwner.mobileNumber ?? undefined,
              dataForms.ownerData?.contact?.additionalMobileNumbers,
              dataForms.ownerData?.contact?.dutyMobileNumber,
              dataForms.formOwner.phoneNumber ?? undefined,
              dataForms.formOwner.address ?? undefined,
              dataForms.formOwner.city ?? undefined,
              dataForms.formOwner.postalCode ?? undefined,
              dataForms.formOwner.country ?? undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              dataForms.formOwner.firstName,
              dataForms.formOwner.lastName,
              optionalGender(dataForms.formOwner.gender),
              dataForms.formOwner.discount ?? undefined,
              dataForms.ownerData?.description ?? undefined,
              dataForms.ownerData?.nickname ?? undefined,
            )
            .pipe(
              switchMap(() =>
                usersService.usersOwnersUidGet(dataForms.formOwner.ownerUid),
              ),
              tap((updatedOwner) => {
                if (updatedOwner.owner) {
                  patchState(store, { owner: updatedOwner.owner });
                }
              }),
              catchError(() => of(null)),
              finalize(() => {
                patchState(store, { statusOwner: LoadingStatus.Loaded });
              }),
            );
        },

        openEditOwnerDialog() {
          const ownerUid = store.ownerUid();
          if (!ownerUid) return;

          const dialogRef = dialog.open(OwnerEditDialogComponent, {
            width: '768px',
            data: { ownerUid },
            autoFocus: false,
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              this.editOwner(result).subscribe();
            }
          });
        },

        editAnimalOnly(data: any) {
          patchState(store, { statusAnimal: LoadingStatus.Loading });

          return animalsService
            .animalsUidPutForm(
              data.animaUid,
              undefined,
              data.profilePictureUid,
              true,
              data.ownerUid,
              data.name,
              data.breed,
              data.gender as Gender,
              data.dateOfBirth
                ? (this.toUTCISOString(data.dateOfBirth) as any)
                : undefined,
              data.dateOfBirth ? undefined : data.birthDateText,
              data.colors ? data.colors.map((c: any) => c.name) : undefined,
              data.microchip,
              data.passportNumber,
              data.passportDate
                ? (this.toUTCISOString(data.passportDate) as any)
                : undefined,
              data.vuIssuingPassports,
              data.warning,
              data.markingDate
                ? (this.toUTCISOString(data.markingDate) as any)
                : undefined,
              data.pedigree,
              data.sterilized,
              data.sterilized === true && data.dateOfSterilization
                ? (this.toUTCISOString(data.dateOfSterilization) as any)
                : undefined,
              data.exitusLetalis,
              undefined,
            )
            .pipe(
              switchMap(() => animalsService.animalsUidGet(data.animaUid)),
              tap((response) => {
                if (response.animal) {
                  patchState(store, { animal: response.animal });
                }
              }),
              catchError(() => of(null)),
              finalize(() => {
                patchState(store, { statusAnimal: LoadingStatus.Loaded });
              }),
            );
        },

        openEditAnimalDialog() {
          const owner = store.owner();
          const animalUid = store.animalUid();
          if (!owner?.uid || !animalUid) return;

          dialog.open(AddEditOwnerDialogComponent, {
            ...PORTAL_DIALOG.medium,
            autoFocus: false,
            disableClose: true,
            data: {
              owner,
              animalUid,
              animalOnly: true,
              saveFn: (result: any) =>
                this.editAnimalOnly(result.formAnimal),
            },
          });
        },

        openPaymentsDialog() {
          tabsStateService.createOpenTabs('Owners');

          ownerCardStateService.setUids(
            store.animalUid(),
            store.ownerUid(),
            false,
          );

          localStorage.setItem('ownerUid', store.ownerUid()!);
          localStorage.setItem('animalUid', store.animalUid()!);
        },

        openAddFormDialog(animalDataSource: AnimalDto) {
          // prevent re-entrancy while another dialog is in the process of opening
          if (store.isFormDialogOpening && store.isFormDialogOpening()) {
            return;
          }

          patchState(store, { isFormDialogOpening: true });

          const dialogRef = dialog.open(AddFormDialogComponent, {
            ...PORTAL_DIALOG.extraWide,
            height: '70vh',
            minHeight: '500px',
            panelClass: 'add-form-dialog--large',
            data: animalDataSource,
            disableClose: true,
            autoFocus: false,
          });

          // When dialog has finished opening, allow adding forms again
          dialogRef
            .afterOpened()
            .pipe(take(1))
            .subscribe(() => {
              patchState(store, { isFormDialogOpening: false });
            });

          dialogRef.afterClosed().subscribe((result) => {
            // ensure flag reset in any case
            patchState(store, { isFormDialogOpening: false });
            if (result) {
              // if dialog returned an updated animal, apply it to the store so UI refreshes
              if (result.updatedAnimal) {
                patchState(store, { animal: result.updatedAnimal });
              }
              patchState(store, {
                refreshForms: store.refreshForms() + 1,
                selectedTabIndex: 2,
              });
            }
          });
        },
        setReportUid(event: any) {
          patchState(store, {
            reportUid: event,
          });
        },
        setTabIndex(index: number) {
          patchState(store, { selectedTabIndex: index });
        },
        back() {
          localStorage.removeItem('animalUid');
          if (!tabsStateService.tabs().includes('Owners')) {
            localStorage.removeItem('ownerUid');
          }
        },

        startDashboardTour() {
          tour.startTour([
            {
              elementId: 'owner-details',
              title: 'Owner Detail',
              message:
                "This section displays basic information about the owner. By clicking the > icon to the right of the owner's name, a detailed view with all owner information is shown.",
              offsetY: 50,
              offsetX: 250,
            },
            {
              elementId: 'animal-details',
              title: 'Animal Detail',
              message:
                "This section displays basic information about the animal. By clicking the > icon to the right of the animal's name, a detailed view with all animal information is shown.",
              offsetY: 50,
              offsetX: 250,
            },

            {
              elementId: 'menu-management',
              title: 'Report and Document Management',
              message:
                'This section allows you to switch between three views: Report, Documents, and Forms. Report displays a table of all reports with options to filter by veterinarian, filter by date range, or search. Each row includes options to edit the report or click the > icon to view detailed information. Documents shows all uploaded documents. A button above the table allows you to upload new ones. Forms lists all added forms, and a button at the top lets you create a new form.',
              offsetY: 50,
              offsetX: 250,
            },
          ]);
        },
      };
    },
  ),
);
